import type { FunctionComponent, ReactNode } from 'react';

import { Container, Group, Stack } from '@mantine/core';

import styles from './Dock.module.scss';

type DockProps = {
  children?: ReactNode;
  size?: string;
  stacked?: boolean;
  alignRight?: boolean;
  color?: 'primary' | 'secondary';
  fixed?: boolean;
  noShadow?: boolean;
  noSticky?: boolean;
  appShellOffset?: boolean;
  isMobile?: boolean;
  px?: string;
  pt?: string;
  pb?: string;
};

export const Dock: FunctionComponent<DockProps> = ({
  children,
  size = 'lg',
  stacked,
  alignRight = false,
  color = 'primary',
  fixed = false,
  noShadow = false,
  noSticky = false,
  appShellOffset = false,
  isMobile = false,
  px,
  pt,
  pb,
}) => {
  const xPadding = px || (size === 'sm' ? 'sm' : 'xl');

  const buttonLayout = stacked ? (
    <Stack
      px={xPadding}
      pt={pt || 'sm'}
      pb={pb || 'sm'}
      gap="xs"
      align="stretch"
      justify="center"
      className={styles.stackedButtons}
    >
      {children}
    </Stack>
  ) : (
    <Group
      px={xPadding}
      pt={pt || 'sm'}
      pb={pb || 'sm'}
      gap="xs"
      grow={!alignRight}
      justify={alignRight ? 'flex-end' : 'flex-start'}
    >
      {children}
    </Group>
  );

  const dockClassName = [
    styles.dock,
    color === 'secondary' && styles.dockSecondary,
    noShadow && styles.dockNoShadow,
    noSticky && styles.dockNoBorder,
  ]
    .filter(Boolean)
    .join(' ');

  const getContainerStyle = () => {
    if (fixed) {
      return {
        bottom: 0,
        left: appShellOffset && !isMobile ? 'var(--app-shell-left-offset, 200px)' : 0,
        right: 0,
        position: 'fixed' as const,
        zIndex: 'var(--mantine-z-index-popover)',
      };
    }
    if (noSticky) return {};
    return { bottom: 0, position: 'sticky' as const, zIndex: 1 };
  };

  const containerStyle = getContainerStyle();

  return (
    <Container p="0" fluid style={containerStyle} className={dockClassName}>
      {buttonLayout}
    </Container>
  );
};
